import { MantineProvider } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks';
import { updateUserSettings } from '@/services';
import {
  buildMantineTheme,
  DEFAULT_THEME_ID,
  THEME_DEFINITIONS,
  type ThemeDefinition,
} from '@/theme';
import type { ThemeId } from '@/types';

export interface ThemeContextValue {
  themeId: ThemeId;
  themeDef: ThemeDefinition;
  /** Switches the active theme immediately and persists it for the signed-in user. */
  setThemeId: (id: ThemeId) => void;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Owns the active theme: defaults to the signed-in user's saved `theme`
 * preference (falling back to Fleet Blueprint for signed-out users or
 * accounts predating this feature), renders Mantine's `MantineProvider`
 * with that theme's palette/color-scheme, and exposes the CSS custom
 * properties `src/index.css` uses for the few things Mantine doesn't theme
 * itself (page background/grid, text selection, scrollbar).
 */
export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { appUser, firebaseUser } = useAuth();
  const { t } = useTranslation();
  const [themeId, setThemeIdState] = useState<ThemeId>(DEFAULT_THEME_ID);

  // Adopt the signed-in user's saved preference once their profile loads
  // (or reset to default on sign-out). Local overrides via `setThemeId`
  // still take effect immediately without waiting on this.
  useEffect(() => {
    if (appUser?.theme && THEME_DEFINITIONS[appUser.theme]) {
      setThemeIdState(appUser.theme);
    } else if (!firebaseUser) {
      setThemeIdState(DEFAULT_THEME_ID);
    }
  }, [appUser?.theme, firebaseUser]);

  const themeDef = THEME_DEFINITIONS[themeId];

  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--app-bg', themeDef.pageBg);
    root.setProperty('--app-grid-line', themeDef.gridLine);
    root.setProperty('--app-text', themeDef.ink[9]);
    root.setProperty('--app-selection-bg', themeDef.selectionBg);
    root.setProperty('--app-selection-text', themeDef.selectionText);
    root.setProperty('--app-scrollbar-track', themeDef.scrollbarTrack);
    root.setProperty('--app-scrollbar-thumb', themeDef.scrollbarThumb);
    root.setProperty('--app-scrollbar-thumb-hover', themeDef.scrollbarThumbHover);
  }, [themeDef]);

  const setThemeId = useCallback(
    (id: ThemeId) => {
      setThemeIdState(id);
      if (firebaseUser) {
        updateUserSettings(firebaseUser.uid, { theme: id }).catch(() => {
          notifications.show({ color: 'red', message: t('profile.errors.saveFailed') });
        });
      }
    },
    [firebaseUser, t],
  );

  const mantineTheme = useMemo(() => buildMantineTheme(themeDef), [themeDef]);
  const contextValue = useMemo<ThemeContextValue>(
    () => ({ themeId, themeDef, setThemeId }),
    [themeId, themeDef, setThemeId],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MantineProvider
        defaultColorScheme={themeDef.colorScheme}
        forceColorScheme={themeDef.colorScheme}
        theme={mantineTheme}
      >
        {children}
      </MantineProvider>
    </ThemeContext.Provider>
  );
};
